import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence, useAnimation } from "framer-motion";
import { Slider } from "@/components/ui/slider";
import InfoBadge from "./InfoBadge";
import { useHapticFeedback, hapticVariants } from "@/hooks/useHapticFeedback";

interface WizardSliderProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step?: number;
  suffix?: string;
  prefix?: string;
  icon: React.ElementType;
  infoBadge?: {
    title: string;
    content: string;
    variant?: 'default' | 'amber' | 'purple' | 'green';
  };
  animated?: boolean;
  keyValues?: number[];
  helperText?: string;
}

const WizardSlider = ({ 
  label, 
  value, 
  onChange, 
  min, 
  max, 
  step = 1,
  suffix = "",
  prefix = "",
  icon: Icon,
  infoBadge,
  animated = false,
  keyValues = [],
  helperText
}: WizardSliderProps) => {
  const [isChanging, setIsChanging] = useState(false);
  const [displayValue, setDisplayValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const controls = useAnimation();
  const sliderControls = useAnimation();
  const lastValueRef = useRef<number>(value);
  const containerRef = useRef<HTMLDivElement>(null);
  const { getHapticIntensity, triggerNativeHaptic } = useHapticFeedback();

  useEffect(() => {
    setDisplayValue(value);
    if (animated) {
      setIsChanging(true);
      const timer = setTimeout(() => setIsChanging(false), 150);
      return () => clearTimeout(timer);
    }
  }, [value, animated]);

  const triggerFeedback = useCallback((newValue: number) => {
    if (newValue !== lastValueRef.current) {
      const intensity = getHapticIntensity(newValue, min, max, keyValues);
      sliderControls.start(hapticVariants[intensity]);
      controls.start({ scale: [1, 1.15, 1], transition: { duration: 0.15 } });
      triggerNativeHaptic(intensity);
      lastValueRef.current = newValue;
    }
  }, [getHapticIntensity, min, max, keyValues, sliderControls, controls, triggerNativeHaptic]);

  const handleSliderChange = (vals: number[]) => {
    const newValue = vals[0];
    triggerFeedback(newValue);
    onChange(newValue);
  };

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    let newValue = value;
    const largeStep = step * 10;
    
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        e.preventDefault();
        newValue = Math.min(max, value + step);
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        e.preventDefault();
        newValue = Math.max(min, value - step);
        break;
      case 'PageUp':
        e.preventDefault();
        newValue = Math.min(max, value + largeStep);
        break;
      case 'PageDown':
        e.preventDefault();
        newValue = Math.max(min, value - largeStep);
        break;
      case 'Home':
        e.preventDefault();
        newValue = min;
        break;
      case 'End':
        e.preventDefault();
        newValue = max;
        break;
      default:
        return;
    }
    
    if (newValue !== value) {
      triggerFeedback(newValue);
      onChange(newValue);
    }
  }, [value, min, max, step, onChange, triggerFeedback]);

  return (
    <motion.div 
      ref={containerRef}
      className={`space-y-3 p-5 sm:p-6 rounded-xl bg-muted/30 border transition-all duration-200 ${
        isFocused ? 'border-brand-500 ring-2 ring-brand-500/20 shadow-md' : 'border-border/50 hover:border-border'
      }`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      tabIndex={0}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onKeyDown={handleKeyDown}
      role="slider"
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={value}
      aria-label={label}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 flex-wrap min-w-0">
          <motion.div 
            className="p-2.5 bg-brand-100 dark:bg-brand-900/30 rounded-lg shrink-0"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Icon className="h-5 w-5 text-brand-600 dark:text-brand-400" />
          </motion.div>
          <div className="min-w-0">
            <label className="text-sm sm:text-base font-semibold text-foreground block">{label}</label>
            {helperText && (
              <p className="text-xs text-muted-foreground mt-0.5 truncate">{helperText}</p>
            )}
          </div>
          {infoBadge && (
            <InfoBadge 
              title={infoBadge.title}
              content={infoBadge.content}
              variant={infoBadge.variant}
            />
          )}
        </div>
        <AnimatePresence mode="wait">
          <motion.span 
            key={displayValue}
            className="text-lg sm:text-xl px-4 py-1.5 font-bold text-brand-600 bg-brand-50 dark:bg-brand-900/30 rounded-full shrink-0"
            initial={animated ? { scale: 1.15, color: "hsl(var(--brand-600))" } : false}
            animate={controls}
            style={{
              scale: isChanging ? 1.1 : 1,
              color: isChanging ? "hsl(25, 95%, 53%)" : undefined
            }}
            transition={{ type: "spring", stiffness: 500, damping: 25 }}
          >
            {prefix}{displayValue.toLocaleString()}{suffix}
          </motion.span>
        </AnimatePresence>
      </div>
      <motion.div
        animate={sliderControls}
        whileHover={{ scale: 1.01 }}
        transition={{ duration: 0.2 }}
        className="py-1"
      >
        <Slider
          value={[value]}
          onValueChange={handleSliderChange}
          min={min}
          max={max}
          step={step}
          className="w-full"
        />
      </motion.div>
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{prefix}{min.toLocaleString()}{suffix}</span>
        <span>{prefix}{max.toLocaleString()}{suffix}</span>
      </div>
    </motion.div>
  );
};

export default WizardSlider;
